import React from "react";
import { FileText, Megaphone, UtensilsCrossed } from "lucide-react";
import { useT, Bi } from "@/lib/i18n";
import { inr, monthLabel, dateLabel } from "@/lib/format";
import { Card } from "@/components/common";
import { StatusPill } from "@/components/StatusPill";

function RentNow({ r }: { r: any }) {
  const { t, lang } = useT();
  if (!r) return null;
  const tone = r.status === "paid" ? "bg-green-700" : r.status === "overdue" ? "bg-rose-600" : "bg-amber-500";
  return (
    <div className={`rounded-3xl ${tone} text-white p-5`} data-testid="public-rent-card">
      <p className="text-xs font-semibold uppercase tracking-wider opacity-80">{t("pub_rent_status")} · {monthLabel(r.month, lang, true)}</p>
      <p className="text-4xl font-extrabold font-mono-num mt-2" data-testid="public-rent-outstanding">{r.outstanding > 0 ? inr(r.outstanding) : inr(r.amount_due)}</p>
      <p className="text-sm opacity-90 mt-1">{r.outstanding > 0 ? `${t("balance")} · ${t("due")} ${dateLabel(r.due_date, lang)}` : t("st_paid")}</p>
    </div>
  );
}

function TodayMenu({ menu }: { menu: any }) {
  const { t } = useT();
  return (
    <Card className="p-4" data-testid="public-menu">
      <p className="font-bold flex items-center gap-2 mb-2"><UtensilsCrossed className="h-4 w-4 text-amber-600" /><Bi k="pub_menu_today" /></p>
      {menu && (menu.breakfast || menu.lunch || menu.dinner) ? (
        <div className="grid grid-cols-3 gap-2">
          {(["breakfast", "lunch", "dinner"] as const).map((m) => (
            <div key={m} className="rounded-xl bg-amber-50/70 border border-amber-100 p-2.5">
              <p className="text-[11px] font-semibold text-amber-800">{t(m)}</p>
              <p className="text-sm mt-0.5">{menu[m] || "-"}</p>
            </div>
          ))}
        </div>
      ) : <p className="text-sm text-slate-500">{t("pub_no_menu")}</p>}
    </Card>
  );
}

export function TenantView({ d, onReceipt }: { d: any; onReceipt: (p: any) => void }) {
  const { t, lang } = useT();
  return (
    <div className="space-y-4 pg-stagger">
      <div>
        <h1 className="text-2xl font-bold" data-testid="public-tenant-name">{t("pub_hello")}, {d.tenant.name}</h1>
        <p className="text-sm text-slate-500">{d.property.name} · {t("room")} {d.tenant.room_name} · {t("bed")} {d.tenant.bed_label} · {inr(d.tenant.monthly_rent)}/mo</p>
      </div>
      <RentNow r={d.records[0]} />
      <TodayMenu menu={d.today_menu} />
      <Card className="p-4" data-testid="public-history">
        <p className="font-bold mb-2"><Bi k="pub_history" /></p>
        {d.records.map((r) => (
          <div key={r.month} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
            <div>
              <p className="text-sm font-semibold">{monthLabel(r.month, lang, true)}</p>
              <p className="text-xs text-slate-500 font-mono-num">{inr(r.paid)} / {inr(r.amount_due)}{r.is_prorated ? ` · ${t("prorated")}` : ""}</p>
            </div>
            <StatusPill status={r.status} testId={`public-status-${r.month}`} />
          </div>
        ))}
      </Card>
      <Card className="p-4" data-testid="public-receipts">
        <p className="font-bold mb-2"><Bi k="pub_receipts" /></p>
        {d.receipts.length === 0 ? <p className="text-sm text-slate-500">{t("no_payments")}</p> : d.receipts.map((p) => (
          <button key={p.id} data-testid={`public-receipt-${p.receipt_no}`} onClick={() => onReceipt(p)}
            className="w-full text-left flex items-center justify-between py-2.5 border-b border-slate-100 last:border-0">
            <div>
              <p className="text-sm font-semibold font-mono-num">{inr(p.amount)} · {monthLabel(p.month, lang)}</p>
              <p className="text-xs text-slate-500">{p.receipt_no} · {dateLabel(p.paid_on, lang)}</p>
            </div>
            <FileText className="h-5 w-5 text-emerald-700" />
          </button>
        ))}
      </Card>
      {d.announcements.length > 0 && (
        <Card className="p-4" data-testid="public-announcements">
          <p className="font-bold flex items-center gap-2 mb-2"><Megaphone className="h-4 w-4 text-indigo-600" /><Bi k="pub_notices" /></p>
          {d.announcements.map((a) => (
            <div key={a.id} className="py-2 border-b border-slate-100 last:border-0">
              <p className="text-sm font-semibold">{a.title}</p>
              {a.body && <p className="text-sm text-slate-600">{a.body}</p>}
            </div>
          ))}
        </Card>
      )}
      <p className="text-xs text-slate-400 text-center">{t("receipt_footer")}</p>
    </div>
  );
}
